import { useDispatch } from "react-redux";
import { Button, Flex, Icon, IconButton, Tooltip, useBreakpointValue, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { showErrorToast, showSuccessToast } from "../../utils";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { FormInput } from "../../components/Forms";
import { FaDownload, FaFileImport, FaQuestionCircle } from "react-icons/fa";
import * as React from "react";

function ImportData({
                      importDataBtnId,
                      importDataHook,
                      importDataParams = null,
                      importDataConfig = null,
                      downloadTemplateHook = null,
                      downloadTemplateParams = null,
                      downloadTemplateConfig = null,
                      tooltip
                    }) {
  const dispatch = useDispatch();
  const toast = useToast();
  const { t } = useTranslation("translation");
  const importBtnId = `import-data-btn-${importDataBtnId}`;

  const templateBtnWidth = useBreakpointValue({
    base: "34",
    md: "48"
  });
  const importBtnWidth = useBreakpointValue({
    base: "28",
    md: "32"
  });
  const btnFontSize = useBreakpointValue({
    base: "sm",
    md: "md"
  });
  const containerDisplay = useBreakpointValue({
    base: "block",
    md: "flex"
  });

  const defaultImportDataConfig = {
    onSuccess: () => showSuccessToast(toast, t("messages.importSuccess")),
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const defaultDownloadTemplateConfig = {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: importData } = importDataHook(importDataConfig ?? defaultImportDataConfig);
  const { mutate: downloadTemplate = () => {} } = downloadTemplateHook?.(downloadTemplateConfig ?? defaultDownloadTemplateConfig) ?? {};

  const handleDataImport = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      dispatch(showAppSpinner());
      document.getElementById(importBtnId).value = null;
      let params = {
        data: formData
      };
      if (importDataParams) {
        params = {
          ...params,
          ...importDataParams
        }
      }
      importData(params);
    }
  };

  const handleImportDataClick = () => {
    const importBtn = document.getElementById(importBtnId);
    if (importBtn) {
      importBtn.click();
    }
  };

  const handleDownloadTemplate = () => {
    dispatch(showAppSpinner());
    downloadTemplate(downloadTemplateParams ?? {});
  };

  return (
    <Flex alignItems="center" gap={2}>
      {downloadTemplateHook ?
        <Button
          colorScheme="white"
          color="esg_lightgreen"
          border={"1px solid"}
          borderColor={"esg_lightgreen"}
          width={templateBtnWidth}
          fontSize={btnFontSize}
          onClick={handleDownloadTemplate}
        >
          <Icon
            as={FaDownload}
            inlineSize="3"
            blockSize="3"
            color="esg_green"
            marginEnd="2"
            marginTop={containerDisplay === "block" ? "0" : "1"}
          />
          {t("downloadTemplate")}
        </Button> : null
      }
      {tooltip ?
        <Tooltip
          label={tooltip}
          whiteSpace={"pre-line"}
          hasArrow
          marginRight={4}
        >
          <IconButton
            aria-label="import-tooltip"
            type="button"
            icon={<FaQuestionCircle size="20" />}
            flexShrink="0"
            size="sm"
            opacity="0.25"
            tabIndex={-1}
          />
        </Tooltip> : null
      }
      <FormInput
        id={importBtnId}
        name={importBtnId}
        type="file"
        onChange={(e) => handleDataImport(e)}
        paddingInlineStart="0"
        border="none"
        accept={".xlsx, .xls"}
        controlProps={{ display: "none" }}
      />
      <Button
        colorScheme="white"
        color="esg_lightgreen"
        border={"1px solid"}
        borderColor={"esg_lightgreen"}
        width={importBtnWidth}
        fontSize={btnFontSize}
        onClick={handleImportDataClick}
        marginRight={5}
        title={tooltip}
      >
        <Icon
          as={FaFileImport}
          inlineSize="3"
          blockSize="3"
          color="esg_green"
          marginEnd="2"
          marginTop={containerDisplay === "block" ? "0" : "1"}
        />
        {t("importData")}
      </Button>
    </Flex>
  );
}

export default ImportData;